/* eslint-disable react/jsx-one-expression-per-line, no-param-reassign, react/display-name, react/no-array-index-key, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-non-null-asserted-optional-chain */
import { Box, HStack, Icon, Stack, Text } from '@chakra-ui/core';
import React, { useRef, useEffect } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { MdOutlineRadioButtonUnchecked, MdOutlineRadioButtonChecked, MdCheckBox, MdCheckBoxOutlineBlank } from 'react-icons/md';
import { ImagePreviewModal } from '../../../../../app/components/ImagePreviewModal';
import ImageView from '../../../../../app/components/ImageView';
import { QUESTIONNAIRE_ELEMENT_TYPE, SCREENING_TYPE } from '../../questionnaireTemplate.enums';
import { QuestionnaireFormElement } from '../../questionnaireTemplate.types';
import { QuestionnaireTemplateStore } from '../../store/questionnaireTemplateModel';
import { formElementSchema } from '../schemas/formElement.schema';

const COLORS = {
  border: '#E5E5E5',
  primary: '#2196F3',
  secondary: '#4D4D4D',
  gray: '#4b5255',
  lightGray: '#FAFAFA',
  placeholder: '#999',
  branchAccent: '#FF9800',
} as const;

export const QuestionnaireListView = React.memo(function QuestionnaireListView() {
  const { t } = useTranslation('questionnaireTemplate');
  const scrollRef = useRef<HTMLDivElement>(null);
  const rootElementIds = QuestionnaireTemplateStore.useStoreState((state) => state.rootElementIds);
  const selectedElementId = QuestionnaireTemplateStore.useStoreState((state) => state.selectedElementId);

  useEffect(() => {
    if (selectedElementId && scrollRef.current) {
      const elementNode = scrollRef.current.querySelector(`[data-question-id="${selectedElementId}"]`);
      if (elementNode) {
        elementNode.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [selectedElementId]);

  return (
    <Box width="100%" height="calc(100vh - 88px)" backgroundColor="#fff" overflow="hidden" display="flex" flexDirection="column">
      <Box
        flex={1}
        overflowY="auto"
        overflowX="hidden"
        ref={scrollRef}
        py={2}
        pr={6}
        sx={{
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}
      >
        {rootElementIds.length === 0 ? (
          <Box p={8} textAlign="center">
            <Text color={COLORS.placeholder} fontSize="sm">
              {t('formBuilder.preview.listView.noQuestions')}
            </Text>
          </Box>
        ) : (
          <Stack spacing={0}>
            {rootElementIds.map((id, index) => (
              <QuestionListItem key={id} questionId={id} questionNumber={`${index + 1}`} />
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
});

// Helper for cleaner JSX
export function renderOptions(elementData: QuestionnaireFormElement, t: TFunction<'questionnaireTemplate'>) {
  const isYesNo =
    elementData.questionType === QUESTIONNAIRE_ELEMENT_TYPE.YESNO ||
    elementData.questionType === QUESTIONNAIRE_ELEMENT_TYPE.YESNO_WITH_IMAGE;
  const isMulti =
    elementData.questionType === QUESTIONNAIRE_ELEMENT_TYPE.MULTI_CHOICE ||
    elementData.questionType === QUESTIONNAIRE_ELEMENT_TYPE.MULTI_CHOICE_WITH_IMAGE;

  if (isYesNo) {
    const elementSchema = formElementSchema[elementData.questionType];
    if (!elementSchema?.branches?.length) return null;
    return (
      <HStack spacing={4} pl={8}>
        {elementSchema.branches?.map((branch, i) => {
          const isPreferredAnswer = branch === elementData.preferredAnswer;
          return (
            <Text key={i} fontSize="xs" fontWeight={isPreferredAnswer ? 'bold' : 'normal'} color={COLORS.gray}>
              {t(`formBuilder.schema.yesno.branches.${i}`)}
            </Text>
          );
        })}
      </HStack>
    );
  }

  const isMultipleChoice = elementData?.totalAnswer === 'any' || (elementData?.totalAnswer ?? 0) > 1;

  if (isMulti && elementData.options) {
    return (
      <Stack spacing={1} pl={8}>
        {elementData.options.map((opt, i: number) => {
          let IconComponent;

          if (isMultipleChoice) {
            IconComponent = opt.isPreferredAnswer ? MdCheckBox : MdCheckBoxOutlineBlank;
          } else {
            IconComponent = opt.isPreferredAnswer ? MdOutlineRadioButtonChecked : MdOutlineRadioButtonUnchecked;
          }

          return (
            <HStack key={i} spacing={1} align="flex-start">
              <Icon as={IconComponent} flexShrink={0} boxSize={4} />

              <Text fontSize="xs" color={COLORS.gray} whiteSpace="pre-line" wordBreak="break-all">
                {opt.text || t('formBuilder.preview.listView.optionTextPlaceholder', { index: i + 1 })}
              </Text>
            </HStack>
          );
        })}
      </Stack>
    );
  }
  return null;
}

interface ItemProps {
  questionId: string;
  questionNumber: string;
}

export const QuestionListItem = React.memo(function QuestionListItem({ questionId, questionNumber }: ItemProps) {
  const { t } = useTranslation('questionnaireTemplate');
  const [open, setOpen] = React.useState(false);
  const selectedElementId = QuestionnaireTemplateStore.useStoreState((state) => state.selectedElementId);
  const ordering = QuestionnaireTemplateStore.useStoreState((state) => state.ordering);

  const elementData = QuestionnaireTemplateStore.useStoreState((state) => state.entities.elements[questionId], [questionId]);

  if (!elementData) return null;

  const displayQuestion = elementData.question || t('formBuilder.preview.listView.noQuestionText');
  const hasNoQuestionText = !elementData.question;
  const isSelected = questionId === selectedElementId;
  const isKnockout = elementData.screeningType === SCREENING_TYPE.KNOKOUT;
  const isPaused = elementData.screeningType === SCREENING_TYPE.REVIEW;

  return (
    <Box width="100%">
      <HStack
        align="flex-start"
        data-question-id={questionId}
        data-testid={`question-${questionId}`}
        px={4}
        py={3}
        backgroundColor={isSelected ? '#E3F2FD' : 'transparent'}
        borderRadius="md"
        cursor="pointer"
        position="relative"
        transition="all 0.2s"
        _hover={{ backgroundColor: isSelected ? '#E3F2FD' : COLORS.lightGray }}
        _before={
          isSelected
            ? {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '4px',
                backgroundColor: COLORS.primary,
              }
            : undefined
        }
      >
        <Stack spacing={2} flex={1}>
          <HStack spacing={1} align="flex-start">
            <Text fontSize="sm" color={COLORS.gray} fontWeight="bold" minW="20px">
              Q{questionNumber}.
            </Text>
            <Text
              fontSize="sm"
              color={hasNoQuestionText ? COLORS.placeholder : COLORS.secondary}
              flex={1}
              fontWeight={isKnockout ? 'bold' : 'normal'}
              fontStyle={isPaused ? 'italic' : 'normal'}
              whiteSpace="pre-line"
              wordBreak="break-word"
            >
              {displayQuestion}
            </Text>
          </HStack>

          {renderOptions(elementData, t)}
        </Stack>
        {elementData.images?.length! > 0 && (
          <HStack justify="center">
            <ImageView
              src={elementData?.images?.[0]?.url}
              showAddImageIcon
              onClick={() => {
                setOpen(true);
              }}
              shape="rounded"
              height="40px"
            />
            <ImagePreviewModal
              setTrigger={open}
              images={elementData?.images}
              getImageKey={/* istanbul ignore next */ (image) => image?.id || ''}
              getImageSrc={/* istanbul ignore next */ (image) => image?.url || ''}
              onModalClose={() => setOpen(false)}
            />
          </HStack>
        )}
      </HStack>

      {elementData?.activeBranches?.length! > 0 &&
        elementData.activeBranches?.map((branchName: string) => {
          const branchKey = `${questionId}-${branchName}`;
          const branchChildren = ordering[branchKey] || [];
          const currentBranchIndex = formElementSchema[elementData.questionType].branches?.indexOf(branchName);

          if (branchChildren.length === 0) return null;

          return (
            <Box key={branchKey} borderLeft={`2px solid ${COLORS.border}`} ml={6} my={1}>
              <Box px={4} py={1}>
                <Text fontSize="xs" color={COLORS.branchAccent} fontWeight="bold" textTransform="uppercase">
                  {t('formBuilder.preview.listView.conditionIf', {
                    condition: t(`formBuilder.schema.yesno.branches.${currentBranchIndex}`),
                  })}
                </Text>
              </Box>
              <Stack spacing={0}>
                {branchChildren.map((childId, childIndex) => (
                  <QuestionListItem key={childId} questionId={childId} questionNumber={`${questionNumber}.${childIndex + 1}`} />
                ))}
              </Stack>
            </Box>
          );
        })}
    </Box>
  );
});
